import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  LogOut, RefreshCw, Calendar, Users, MessageSquare, BarChart3,
  Phone, Mail, MapPin, Clock, Baby, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";

const STATUSES = ["Pending", "Confirmed", "Completed", "Cancelled"];
const OWNERS = ["Jess", "Jade", "Shannon"];
const APP_STATUSES = ["New", "Reviewing", "Screening", "Approved", "Rejected"];

const statusColor = (s) => ({
  Pending: "bg-amber-100 text-amber-800",
  Confirmed: "bg-emerald-100 text-emerald-800",
  Completed: "bg-stone-200 text-stone-700",
  Cancelled: "bg-red-100 text-red-800",
  New: "bg-blue-100 text-blue-800",
  Reviewing: "bg-amber-100 text-amber-800",
  Screening: "bg-purple-100 text-purple-800",
  Approved: "bg-emerald-100 text-emerald-800",
  Rejected: "bg-red-100 text-red-800",
}[s] || "bg-stone-100 text-stone-700");

function Stat({ label, value, icon: Icon }) {
  return (
    <div className="bg-white rounded-2xl border border-[#F1E6D0] p-5 flex items-center gap-4" data-testid={`stat-${label}`}>
      <span className="w-11 h-11 rounded-full bg-tinted text-terracotta flex items-center justify-center"><Icon className="w-5 h-5" /></span>
      <div><div className="font-serif text-2xl text-espresso">{value}</div><div className="text-xs uppercase tracking-wider text-stoneish">{label}</div></div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [applicants, setApplicants] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterOwner, setFilterOwner] = useState("all");

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus !== "all") params.status = filterStatus;
      if (filterOwner !== "all") params.owner = filterOwner;
      const [b, a, c, an] = await Promise.all([
        api.get("/admin/bookings", { params }),
        api.get("/admin/applicants"),
        api.get("/admin/contacts"),
        api.get("/admin/analytics"),
      ]);
      setBookings(b.data); setApplicants(a.data); setContacts(c.data); setAnalytics(an.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterOwner]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const updateBooking = async (id, field, value) => {
    try {
      const { data } = await api.patch(`/admin/bookings/${id}`, { [field]: value });
      setBookings((prev) => prev.map((b) => (b.id === id ? data : b)));
      toast.success(`Booking ${field} updated`);
      api.get("/admin/analytics").then((r) => setAnalytics(r.data)).catch(() => {});
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const updateApplicant = async (id, value) => {
    try {
      const { data } = await api.patch(`/admin/applicants/${id}`, { status: value });
      setApplicants((prev) => prev.map((a) => (a.id === id ? data : a)));
      toast.success("Applicant updated");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const doLogout = async () => { await logout(); navigate("/admin/login"); };

  return (
    <>
      <Seo title="Admin Dashboard" description="Staff dashboard" />
      <div className="min-h-screen bg-parchment">
        {/* Top bar */}
        <header className="bg-espresso text-parchment sticky top-0 z-40">
          <div className="container-mbf py-4 flex items-center justify-between">
            <div>
              <div className="font-serif text-lg text-white">MBF Staff Dashboard</div>
              <div className="text-xs text-parchment/60">{user?.email}</div>
            </div>
            <div className="flex items-center gap-3">
              <button onClick={loadAll} className="p-2 hover:text-gold transition-colors" aria-label="Refresh" data-testid="admin-refresh-btn"><RefreshCw className={`w-5 h-5 ${loading ? "animate-spin" : ""}`} /></button>
              <button onClick={doLogout} className="flex items-center gap-2 text-sm bg-parchment/10 hover:bg-terracotta px-4 py-2 rounded-full transition-colors" data-testid="admin-logout-btn"><LogOut className="w-4 h-4" /> Sign out</button>
            </div>
          </div>
        </header>

        <div className="container-mbf py-8">
          {/* Stats */}
          {analytics && (
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
              <Stat label="Total Bookings" value={analytics.total_bookings} icon={Calendar} />
              <Stat label="Pending" value={analytics.pending} icon={Clock} />
              <Stat label="Confirmed" value={analytics.confirmed} icon={Calendar} />
              <Stat label="Applicants" value={analytics.applicants} icon={Users} />
              <Stat label="Messages" value={analytics.contacts} icon={MessageSquare} />
            </div>
          )}

          <Tabs defaultValue="bookings">
            <TabsList className="bg-tinted mb-6 w-full justify-start overflow-x-auto flex-nowrap whitespace-nowrap">
              <TabsTrigger value="bookings" className="shrink-0" data-testid="tab-bookings"><Calendar className="w-4 h-4 mr-2" />Bookings</TabsTrigger>
              <TabsTrigger value="applicants" className="shrink-0" data-testid="tab-applicants"><Users className="w-4 h-4 mr-2" />Applicants</TabsTrigger>
              <TabsTrigger value="contacts" className="shrink-0" data-testid="tab-contacts"><MessageSquare className="w-4 h-4 mr-2" />Messages</TabsTrigger>
              <TabsTrigger value="analytics" className="shrink-0" data-testid="tab-analytics"><BarChart3 className="w-4 h-4 mr-2" />Analytics</TabsTrigger>
            </TabsList>

            {/* BOOKINGS */}
            <TabsContent value="bookings">
              <div className="flex flex-wrap gap-3 mb-5">
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="w-44 bg-white" data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
                  <SelectContent><SelectItem value="all">All statuses</SelectItem>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={filterOwner} onValueChange={setFilterOwner}>
                  <SelectTrigger className="w-44 bg-white" data-testid="filter-owner"><SelectValue placeholder="Owner" /></SelectTrigger>
                  <SelectContent><SelectItem value="all">All owners</SelectItem>{OWNERS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
              </div>

              {loading ? (
                <div className="py-20 text-center"><Loader2 className="w-8 h-8 animate-spin text-terracotta mx-auto" /></div>
              ) : bookings.length === 0 ? (
                <div className="bg-white rounded-2xl border border-[#F1E6D0] p-16 text-center text-stoneish" data-testid="bookings-empty">No booking requests yet.</div>
              ) : (
                <div className="space-y-4">
                  {bookings.map((b) => (
                    <div key={b.id} className="bg-white rounded-2xl border border-[#F1E6D0] p-6" data-testid={`booking-row-${b.id}`}>
                      <div className="flex flex-wrap items-start justify-between gap-4">
                        <div className="flex-1 min-w-[240px]">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="font-serif text-lg text-espresso">{b.service}</span>
                            <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${statusColor(b.status)}`}>{b.status}</span>
                          </div>
                          <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm text-stoneish">
                            <span className="flex items-center gap-2"><Calendar className="w-3.5 h-3.5" /> {b.start_date} · {b.start_time}</span>
                            <span className="flex items-center gap-2"><Clock className="w-3.5 h-3.5" /> {b.duration_hours} hrs</span>
                            <span className="flex items-center gap-2"><Baby className="w-3.5 h-3.5" /> {b.num_children} child(ren){b.child_ages ? ` · ${b.child_ages}` : ""}</span>
                            <span className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5" /> {b.city}{b.address ? ` · ${b.address}` : ""}</span>
                            <span className="flex items-center gap-2 font-medium text-espresso">{b.contact_name}</span>
                            <span className="flex items-center gap-2"><Phone className="w-3.5 h-3.5" /> {b.phone}</span>
                            <span className="flex items-center gap-2 col-span-2"><Mail className="w-3.5 h-3.5" /> {b.email} · prefers {b.preferred_contact}</span>
                          </div>
                          {b.special_instructions && <p className="mt-3 text-sm bg-tinted rounded-lg p-3 text-stoneish">"{b.special_instructions}"</p>}
                        </div>
                        <div className="flex flex-col gap-2 w-full sm:w-auto">
                          <Select value={b.status} onValueChange={(v) => updateBooking(b.id, "status", v)}>
                            <SelectTrigger className="w-full sm:w-40" data-testid={`booking-status-${b.id}`}><SelectValue /></SelectTrigger>
                            <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                          </Select>
                          <Select value={b.owner} onValueChange={(v) => updateBooking(b.id, "owner", v)}>
                            <SelectTrigger className="w-full sm:w-40" data-testid={`booking-owner-${b.id}`}><SelectValue placeholder="Assign owner" /></SelectTrigger>
                            <SelectContent>{OWNERS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* APPLICANTS */}
            <TabsContent value="applicants">
              {applicants.length === 0 ? (
                <div className="bg-white rounded-2xl border border-[#F1E6D0] p-16 text-center text-stoneish" data-testid="applicants-empty">No applicants yet.</div>
              ) : (
                <div className="grid md:grid-cols-2 gap-4">
                  {applicants.map((a) => (
                    <div key={a.id} className="bg-white rounded-2xl border border-[#F1E6D0] p-6" data-testid={`applicant-row-${a.id}`}>
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <div>
                          <div className="font-serif text-lg text-espresso">{a.full_name}</div>
                          <div className="text-sm text-stoneish">{a.neighborhood} · {a.years_experience}</div>
                        </div>
                        <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${statusColor(a.status)}`}>{a.status}</span>
                      </div>
                      <div className="text-sm text-stoneish space-y-1 mb-3">
                        <div className="flex items-center gap-2"><Mail className="w-3.5 h-3.5" /> {a.email}</div>
                        <div className="flex items-center gap-2"><Phone className="w-3.5 h-3.5" /> {a.phone}</div>
                        <div className="flex gap-3 text-xs mt-2">
                          <span className={a.cpr_certified ? "text-emerald-700" : "text-stoneish"}>CPR {a.cpr_certified ? "✓" : "✗"}</span>
                          <span className={a.driving_license ? "text-emerald-700" : "text-stoneish"}>License {a.driving_license ? "✓" : "✗"}</span>
                          {a.education && <span>{a.education}</span>}
                        </div>
                      </div>
                      {a.bio && <p className="text-sm bg-tinted rounded-lg p-3 text-stoneish mb-3">{a.bio}</p>}
                      <Select value={a.status} onValueChange={(v) => updateApplicant(a.id, v)}>
                        <SelectTrigger className="w-full" data-testid={`applicant-status-${a.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{APP_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* CONTACTS */}
            <TabsContent value="contacts">
              {contacts.length === 0 ? (
                <div className="bg-white rounded-2xl border border-[#F1E6D0] p-16 text-center text-stoneish" data-testid="contacts-empty">No messages yet.</div>
              ) : (
                <div className="space-y-4">
                  {contacts.map((c) => (
                    <div key={c.id} className="bg-white rounded-2xl border border-[#F1E6D0] p-6" data-testid={`contact-row-${c.id}`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-serif text-lg text-espresso">{c.name}</span>
                        <span className="text-xs text-stoneish">{new Date(c.created_at).toLocaleString()}</span>
                      </div>
                      <div className="text-sm text-stoneish flex flex-wrap gap-4 mb-3">
                        <span className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /> {c.email}</span>
                        {c.phone && <span className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" /> {c.phone}</span>}
                        <span className="font-medium text-espresso">{c.subject}</span>
                      </div>
                      <p className="text-sm bg-tinted rounded-lg p-3 text-stoneish">{c.message}</p>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* ANALYTICS */}
            <TabsContent value="analytics">
              {analytics && (
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-white rounded-2xl border border-[#F1E6D0] p-6">
                    <h3 className="font-serif text-lg text-espresso mb-4">Bookings by service</h3>
                    {analytics.by_service.length === 0 ? <p className="text-sm text-stoneish">No data yet.</p> : (
                      <div className="space-y-3">
                        {analytics.by_service.map((s) => {
                          const max = Math.max(...analytics.by_service.map((x) => x.count));
                          return (
                            <div key={s.service}>
                              <div className="flex justify-between text-sm mb-1"><span className="text-espresso">{s.service}</span><span className="text-stoneish">{s.count}</span></div>
                              <div className="h-2 bg-tinted rounded-full overflow-hidden"><div className="h-full bg-terracotta rounded-full" style={{ width: `${(s.count / max) * 100}%` }} /></div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  <div className="bg-white rounded-2xl border border-[#F1E6D0] p-6">
                    <h3 className="font-serif text-lg text-espresso mb-4">Engagement events</h3>
                    {analytics.events.length === 0 ? <p className="text-sm text-stoneish">No events tracked yet.</p> : (
                      <ul className="space-y-2 text-sm">
                        {analytics.events.map((e) => (
                          <li key={e.name} className="flex justify-between border-b border-[#F1E6D0] pb-2 last:border-0">
                            <span className="text-espresso capitalize">{e.name.replace(/_/g, " ")}</span>
                            <span className="font-semibold text-terracotta">{e.count}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </>
  );
}
